import type { ReactNode } from 'react';
import { Link } from './Link';
import { useBillingMode } from '../lib/useBillingMode';

/**
 * Educational block shared by the public Pricing page and the in-app Subscription page.
 * Three sub-sections:
 *
 *  1. Feature comparison matrix — Basic vs Verify (✓/✗ per row). Helps a prospect see at
 *     a glance what the tier includes without having to read four plan cards. Stays the
 *     same in both billing modes (the features are still features).
 *
 *  2. Credits anatomy — explains the budget model. Wallet mode renders WalletAnatomy
 *     (one credit pool + feature gates + credit packs); legacy mode renders the original
 *     "verification credits + anomaly bonus" anatomy. This is the section the plan-card
 *     tooltips deep-link to via `#how-credits-work`.
 *
 *  3. FAQ — different wording per billing mode so the answers match what the tenant
 *     actually experiences ("what happens when wallet empties" vs "what happens when
 *     verification credits exhaust").
 *
 * Same JSX used in both surfaces so the public marketing story and the in-app
 * upgrade-prompt story can't drift apart.
 */
export function PlansExplainer({
  /** Slightly tighter spacing when used inside the in-app page (already inside a Card column). */
  variant = 'public',
}: {
  variant?: 'public' | 'portal';
}) {
  const walletEnabled = useBillingMode().unifiedWalletEnabled;
  const pad = variant === 'portal' ? 'mt-10' : 'mt-16';
  return (
    <section className={pad}>
      <FeatureMatrix walletEnabled={walletEnabled} />
      {walletEnabled ? <WalletAnatomy /> : <CreditsAnatomy />}
      {walletEnabled ? <WalletFaq /> : <PlansFaq />}
    </section>
  );
}

interface MatrixRow {
  label: string;
  hint?: string;
  basic: boolean | string;
  verify: boolean | string;
}

/**
 * Legacy-mode rows — per-feature quotas + slider add-ons + plan-bundled inclusions.
 * The "Anomaly bonus (+20%)" row is legacy-only; under the wallet there's a single
 * credit pool with no separate bonus concept.
 */
const LEGACY_ROWS: ReadonlyArray<MatrixRow> = [
  { label: 'Bot Anomaly Detection + Email Verification', hint: 'Merged into one always-coupled action — every /v1/emails call runs the full stack (near-duplicate clustering + structural risk scoring + authoritative mailbox check). Both tiers run the merged action; there is no anomaly-only mode.', basic: true, verify: true },
  { label: 'Risk score + reasons', hint: '0–100 score per address with human-readable reasons. Drives the allow/verify/review action.', basic: true, verify: true },
  { label: 'Manual review queue', hint: 'Records that score in the review band sit pending until a human accepts/declines.', basic: true, verify: true },
  { label: 'Spamtrap detection', hint: 'Bundled inside the coupled screening — no extra charge on either tier. Spamtrap addresses are forced to review regardless of other signals.', basic: true, verify: true },
  { label: 'Disposable-address flagging', hint: 'Both tiers use the same authoritative disposable-domain list surfaced by the mailbox verifier.', basic: 'Provider-grade', verify: 'Provider-grade' },
  { label: 'Blacklist checks', hint: '50+ industry blacklists checked on every domain you ingest. Pro plans bundle a monthly quota; add more via the slider on Profile → Add-ons.', basic: 'Add-on slider', verify: 'Bundled + slider' },
  { label: 'Inbox-placement tests', hint: 'Send-to-seed-list tests that report inbox vs spam vs missing per ESP. Pro plans bundle a small quota; more via slider.', basic: 'Add-on slider', verify: 'Bundled + slider' },
  { label: 'Phone validation', hint: 'Country / carrier / line-type lookup per number. Pro plans bundle a monthly quota; more via slider.', basic: 'Add-on slider', verify: 'Bundled + slider' },
  { label: 'Domain Health (SPF · DKIM · DMARC · MX)', hint: 'Per-domain deliverability posture. Plan-bundled on Pro at no per-check cost; not available on Basic.', basic: false, verify: 'Plan-bundled' },
  { label: 'Warmup mailboxes', hint: 'Auto-warm sending mailboxes. Pro-only — Pro 50k / 100k bundle 2–3 Starter mailboxes for free, extras billed per-mailbox by tier. Basic plans have no warmup access; upgrade to Pro to unlock.', basic: false, verify: 'Bundle + per-mailbox' },
  { label: 'Outbound webhooks (emails.scored, email.reviewed, email.duplicate_detected)', basic: true, verify: true },
  { label: 'Slack / Email / Intercom alerts', basic: true, verify: true },
  { label: 'Real-time updates (SSE)', basic: true, verify: true },
  { label: 'REST API + multiple keys', basic: true, verify: true },
  { label: 'Audit log + API activity log', basic: true, verify: true },
];

/**
 * Wallet-mode rows — one credit pool, per-feature drain weights, feature gates on
 * Basic. Credit-pack top-ups extend capacity (more credits) but never unlock a
 * gated feature — so a Basic tenant paying for a pack still can't run verification.
 * That's why the "Credit pack top-up" row shows different scopes per tier.
 */
const WALLET_ROWS: ReadonlyArray<MatrixRow> = [
  { label: 'Bot Anomaly Detection + Email Verification', hint: 'Merged into one always-coupled action. Every /v1/emails call runs near-duplicate clustering, structural risk scoring, AND the authoritative mailbox-level check (deliverable / invalid / disposable / spamtrap). Draws from your credit wallet at the combined per-call weight (see per-action cost table).', basic: 'Wallet-metered', verify: 'Wallet-metered' },
  { label: 'Risk score + reasons', hint: '0–100 score per address with human-readable reasons. Included in every screening — no separate charge.', basic: true, verify: true },
  { label: 'Manual review queue', hint: 'Records that score in the review band sit pending until a human accepts/declines.', basic: true, verify: true },
  { label: 'Spamtrap detection', hint: 'Bundled inside the coupled screening action — no extra charge. Spamtrap addresses harm sender reputation; they\'re forced to review regardless of other signals.', basic: true, verify: true },
  { label: 'Disposable-address flagging', hint: 'Both tiers use the same authoritative disposable-domain list surfaced by the mailbox verifier (kept fresh).', basic: 'Provider-grade', verify: 'Provider-grade' },
  { label: 'Blacklist checks', hint: '50+ industry blacklists per domain. Paid from your credit wallet at the blacklist weight.', basic: false, verify: 'Wallet-metered' },
  { label: 'Inbox-placement tests', hint: 'Send-to-seed-list tests reporting inbox vs spam vs missing per ESP. Paid from your credit wallet at the inbox-placement weight.', basic: false, verify: 'Wallet-metered' },
  { label: 'Phone validation', hint: 'Country / carrier / line-type lookup per number. Paid from your credit wallet at the phone-validation weight.', basic: false, verify: 'Wallet-metered' },
  { label: 'Domain Health (SPF · DKIM · DMARC · MX)', hint: 'Per-domain deliverability posture. Paid from your credit wallet at the domain-health weight (typically the lightest per-action cost).', basic: false, verify: 'Wallet-metered' },
  { label: 'Warmup mailboxes', hint: 'Auto-warm sending mailboxes. Pro-only, billed per-mailbox by tier OUTSIDE the credit wallet. Pro 50k / 100k include 2–3 Starter mailboxes for free. Basic plans have no warmup access; upgrade to Pro to unlock.', basic: false, verify: 'Bundle + per-mailbox' },
  { label: 'Credit pack top-up', hint: 'One-shot Stripe purchase that adds credits to your wallet. Never expires. Credit packs EXTEND capacity but do NOT unlock feature gates — a Basic tenant with a pack still can\'t run blacklist / IP / phone / DH.', basic: 'Extends screening + verification', verify: 'Extends full-feature wallet' },
  { label: 'Outbound webhooks (emails.scored, email.reviewed, email.duplicate_detected)', basic: true, verify: true },
  { label: 'Slack / Email / Intercom alerts', basic: true, verify: true },
  { label: 'Real-time updates (SSE)', basic: true, verify: true },
  { label: 'REST API + multiple keys', basic: true, verify: true },
  { label: 'Audit log + API activity log', basic: true, verify: true },
];

function FeatureMatrix({ walletEnabled }: { walletEnabled: boolean }) {
  const rows = walletEnabled ? WALLET_ROWS : LEGACY_ROWS;
  return (
    <div>
      <h2 className="font-display text-2xl font-bold tracking-tight">What's in each tier</h2>
      <p className="mt-2 text-[14px] text-muted">
        {walletEnabled ? (
          <>
            Bot Anomaly Detection + Email Verification is the core product — one always-coupled
            action every /v1/emails call runs, on every tier. Pro plans additionally unlock
            blacklist, inbox placement, phone, and domain health from the same credit wallet;
            Basic plans keep the wallet gated to the coupled screening action only. Rows marked{' '}
            <span className="font-mono text-[12px] font-semibold text-accent">Wallet-metered</span>{' '}
            draw from the same monthly credit pool at the per-action weights shown in the
            per-action cost table above.
          </>
        ) : (
          <>
            Bot Anomaly Detection + Email Verification is the core product — one always-coupled
            action every /v1/emails call runs, on every tier. Pro plans layer on blacklist,
            inbox placement, phone, and domain health via slider add-ons; Basic focuses on the
            coupled screening only.
          </>
        )}
      </p>

      <div className="mt-5 overflow-x-auto rounded-2xl border border-bordersoft">
        <table className="w-full min-w-[560px] text-[13.5px]">
          <thead className="bg-panel2 text-[11px] uppercase tracking-[0.12em] text-faint">
            <tr>
              <th className="px-5 py-3 text-left font-medium">Feature</th>
              <th className="px-5 py-3 text-center font-medium">Basic</th>
              <th className="px-5 py-3 text-center font-medium">Pro</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-t border-bordersoft">
                <td className="px-5 py-3">
                  <span className="text-txt">{r.label}</span>
                  {r.hint && (
                    <p className="mt-0.5 max-w-md text-[12px] leading-relaxed text-faint">
                      {r.hint}
                    </p>
                  )}
                </td>
                <td className="px-5 py-3 text-center">
                  <Cell value={r.basic} />
                </td>
                <td className="px-5 py-3 text-center">
                  <Cell value={r.verify} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Cell({ value }: { value: boolean | string }) {
  if (value === true) return <span className="font-mono text-base text-allow">✓</span>;
  if (value === false) return <span className="font-mono text-base text-faint">—</span>;
  // Wallet-metered cells get their own pill so a reader can scan "which rows draw
  // from credits vs which are free/plan-bundled" without reading every hint.
  if (value === 'Wallet-metered') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md border border-accentdeep/30 bg-accent/5 px-2 py-0.5 font-mono text-[11px] font-semibold text-accent">
        <span className="text-allow">✓</span> Wallet-metered
      </span>
    );
  }
  return <span className="font-mono text-[12px] font-semibold text-accent">{value}</span>;
}

function CreditsAnatomy() {
  return (
    <div id="how-credits-work" className="mt-14 scroll-mt-24">
      <h2 className="font-display text-2xl font-bold tracking-tight">How credits work</h2>
      <p className="mt-2 max-w-2xl text-[14px] text-muted">
        Plans have two counters. Most callers never have to think about them — defaults
        just work. They matter when you want to control cost or your integration needs
        to know whether mailbox verification ran.
      </p>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <CreditCard
          tone="accent"
          label="Verification credits"
          subtitle="Mailbox-level verification budget"
          body={
            <>
              Used when a request runs the mailbox-level check. One credit per cache-miss
              address.
              <br />
              Examples:
              <ul className="mt-2 list-disc pl-4">
                <li>
                  Pro 25k plan → 25,000 verifications per calendar month
                </li>
                <li>Trial (verify-enabled) → the trial verifier budget</li>
                <li>Basic plan → 0 — Basic doesn't include mailbox-level verification</li>
              </ul>
            </>
          }
        />
        <CreditCard
          tone="allow"
          label="Anomaly-only screenings"
          subtitle="Bot/cluster detection budget"
          body={
            <>
              Used by every screening request. Under the legacy per-feature regime this was the
              anomaly-only counter; today Bot Anomaly Detection + Email Verification are one
              coupled action, so this budget always runs alongside the verification budget.
              <br />
              Examples:
              <ul className="mt-2 list-disc pl-4">
                <li>Basic 10k plan → 10,000 coupled screenings per month</li>
                <li>Pro 25k plan → 5,000 (bonus) over and above the 25k verifications</li>
                <li>Trial → independent anomaly trial budget</li>
              </ul>
            </>
          }
        />
      </div>

      <div className="mt-6 rounded-2xl border border-bordersoft bg-panel2 p-5 text-[13.5px] leading-relaxed text-muted">
        <strong className="text-txt">One coupled action.</strong> Every <code>/v1/emails</code>{' '}
        call runs bot/cluster detection AND mailbox verification — no per-request opt-out. The
        response carries both the risk score and the mailbox-verification fields for every
        address in the same shape.
        <br />
        <br />
        <strong className="text-txt">Exhaustion behaviour.</strong> When you've used your
        monthly verification budget mid-month, the API either silently downgrades to
        anomaly-only (your code keeps working — the response carries{' '}
        <code>verifierSkippedReason: 'quota_exhausted'</code>) or returns 402 — your choice,
        configurable in your tenant profile.
      </div>
    </div>
  );
}

function CreditCard({
  tone,
  label,
  subtitle,
  body,
}: {
  tone: 'accent' | 'allow';
  label: string;
  subtitle: string;
  body: ReactNode;
}) {
  const accent = tone === 'accent' ? 'text-accent' : 'text-allow';
  return (
    <div className="rounded-2xl border border-bordersoft bg-panel p-5">
      <div className={`font-mono text-[10.5px] uppercase tracking-[0.14em] ${accent}`}>
        {subtitle}
      </div>
      <div className="mt-1 text-base font-semibold text-txt">{label}</div>
      <div className="mt-3 text-[13px] leading-relaxed text-muted">{body}</div>
    </div>
  );
}

/**
 * Wallet-mode replacement for CreditsAnatomy. Three pillars:
 *   1. Monthly plan wallet — resets on the 1st, drained by every billable action at
 *      the per-action cost shown on the pricing page's "How credits work" panel.
 *   2. Purchased credits — roll over indefinitely; bought via one-shot credit packs.
 *      Stack on top of the monthly allowance.
 *   3. Feature gates — Basic plans cap the wallet to detection only; Pro plans
 *      unlock the full surface (verification + blacklist + IP + phone). Matches the
 *      `creditFeatureGates` field we seed on each plan.
 */
function WalletAnatomy() {
  return (
    <div id="how-credits-work" className="mt-14">
      <h2 className="font-display text-2xl font-bold tracking-tight">
        How the SieveGuard-Credits wallet works
      </h2>
      <p className="mt-2 max-w-2xl text-[14px] text-muted">
        One pool, every feature. Each billable action — detection, verification, blacklist
        check, inbox-placement test, phone lookup — draws credits from your wallet at the
        per-action cost shown on the pricing page. Mix and match against your monthly volume.
      </p>

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        <CreditCard
          tone="accent"
          label="Monthly allowance"
          subtitle="Resets on the 1st"
          body={
            <>
              The credit pool that comes bundled with your plan tier. Spent down by every
              billable action; whatever's left at month-end is discarded (no rollover).
              <ul className="mt-2 list-disc pl-4">
                <li>Pro 75k plan → 75,000 credits / mo (full feature surface)</li>
                <li>Basic 25k plan → 25,000 credits / mo (Bot Detection + Verification only)</li>
                <li>Pro 300k plan → 300,000 credits / mo + 3 warm-up mailboxes</li>
              </ul>
            </>
          }
        />
        <CreditCard
          tone="allow"
          label="Purchased credits"
          subtitle="Roll over indefinitely"
          body={
            <>
              Top-up balance from credit packs. Stacks on top of the monthly allowance and
              never expires.
              <ul className="mt-2 list-disc pl-4">
                <li>Starter Pack — 5,000 credits</li>
                <li>Growth Pack — 25,000 credits</li>
                <li>Power Pack — 100,000 credits</li>
                <li>Scale Pack — 500,000 credits</li>
              </ul>
            </>
          }
        />
        <CreditCard
          tone="accent"
          label="Feature gates"
          subtitle="What the wallet can buy"
          body={
            <>
              Each plan defines which features its credits can be spent on:
              <ul className="mt-2 list-disc pl-4">
                <li>
                  <strong>Basic tiers</strong> — Bot Detection + Verification only.
                  Blacklist, inbox placement, phone, and domain health return 402 with
                  an "upgrade to Pro" hint.
                </li>
                <li>
                  <strong>Pro tiers</strong> — full feature surface. Spend your credits
                  however you like.
                </li>
              </ul>
            </>
          }
        />
      </div>

      <div className="mt-6 rounded-2xl border border-bordersoft bg-panel2 p-5 text-[13.5px] leading-relaxed text-muted">
        <strong className="text-txt">Drain order.</strong> Actions draw from your monthly
        allowance first; once that's empty, purchased credits start covering the bill. At
        month-end any over-allowance spend is debited from purchased credits — you never lose
        a credit you paid for.
        <br />
        <br />
        <strong className="text-txt">Exhaustion behaviour.</strong> When the wallet is empty
        (monthly + purchased both at 0), every billable action returns{' '}
        <code>402 Payment Required</code> with the remaining-balance number, so your client
        code can prompt the user to top up. Detection, verification, and the rest all use
        the same error shape.
      </div>
    </div>
  );
}

function PlansFaq() {
  const { chargeUpgradesImmediately } = useBillingMode();
  const items: Array<{ q: string; a: ReactNode }> = [
    {
      q: 'Will my code break if I switch from Verify to Basic?',
      a: (
        <>
          No. On a Basic plan, requests still succeed — the <code>verification</code> field
          on every result will just be <code>null</code> with{' '}
          <code>verifierSkippedReason: 'plan_no_verifier'</code>. Branch on that if your flow
          cares; ignore it otherwise.
        </>
      ),
    },
    {
      q: 'When does Stripe charge me?',
      a: (
        <>
          The first invoice fires at checkout for the full plan fee (pay-first,
          use-second), then again on each renewal date — monthly or annually per the
          interval you picked. Yearly billing is 10% off, and your{' '}
          <strong>first yearly invoice</strong> gets a one-time "2 months free" coupon
          (≈17% off in total for year one; renewals charge the standard 10%-off rate).{' '}
          {chargeUpgradesImmediately ? (
            <>
              <strong>Mid-cycle upgrades</strong> (plan change or added add-on tier) are
              charged immediately as a separate invoice; downgrades credit the unused
              portion onto your next invoice.
            </>
          ) : (
            <>
              <strong>Mid-cycle changes</strong> pro-rate onto your next invoice.
            </>
          )}
        </>
      ),
    },
    {
      q: 'Do prices include VAT?',
      a: (
        <>
          Prices on this page are <strong>excl. VAT</strong>. Stripe Tax applies the right rule
          per customer at checkout: <strong>EU businesses</strong> with a valid VAT ID pay no
          VAT (reverse charge); <strong>non-EU customers</strong> pay no VAT (export of
          services); <strong>consumers</strong> and EU buyers without a VAT ID pay VAT at their
          country's rate. Your VAT ID is best added under Settings → Billing before checkout,
          but you can also enter it on the Stripe payment screen.
        </>
      ),
    },
    {
      q: 'Can I top up mid-month?',
      a: (
        <>
          Upgrade your plan from the portal — the new monthly allowance applies immediately.
          Your existing in-flight requests are unaffected.
        </>
      ),
    },
    {
      q: 'What if I exceed the monthly emails cap?',
      a: (
        <>
          Requests return <code>402 Payment Required</code> until the next monthly reset OR
          you upgrade. Every /v1/emails call counts against the cap — Bot Anomaly Detection
          + Email Verification are one coupled action.
        </>
      ),
    },
  ];

  return (
    <div className="mt-14">
      <h2 className="font-display text-2xl font-bold tracking-tight">Common questions</h2>
      <div className="mt-5 grid gap-3 md:grid-cols-2">
        {items.map((it) => (
          <div
            key={it.q}
            className="rounded-2xl border border-bordersoft bg-panel p-5 text-[13.5px]"
          >
            <div className="font-semibold text-txt">{it.q}</div>
            <div className="mt-1.5 leading-relaxed text-muted">{it.a}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Wallet-mode FAQ. Different questions from the legacy FAQ because the budget model is
 * different — covers wallet exhaustion, credit-pack top-up, plan upgrades vs packs, and
 * the per-feature gate ("why can't my Basic-tier credits run inbox placement?").
 */
function WalletFaq() {
  const { chargeUpgradesImmediately } = useBillingMode();
  const items: Array<{ q: string; a: ReactNode }> = [
    {
      q: 'What does one credit buy?',
      a: (
        <>
          A credit is the unit of metering across every billable feature. The exact
          per-action weights are in the per-action cost table above (they may drift as
          the platform tunes for margin). Broadly, one signup screening (Bot Anomaly
          Detection + Email Verification, always coupled on <code>/v1/emails</code>) is
          the cheapest paid action, Domain Health is a fair-share guard weight,
          Phone Validation sits next, Blacklist Checks are heavier, and Inbox Placement
          is the priciest single call because it fires real mail to real seed mailboxes.
        </>
      ),
    },
    {
      q: 'What if I run out of credits mid-month?',
      a: (
        <>
          Buy a credit pack from{' '}
          <Link to="/app/billing/credit-packs" className="text-accent hover:underline">
            Billing → Credit packs
          </Link>{' '}
          — top-up balance is added the moment Stripe confirms payment, no waiting. If you
          consistently run out mid-month it's usually cheaper to upgrade the plan tier;
          packs are priced for occasional spikes, not as a primary spend channel.
        </>
      ),
    },
    {
      q: "Why can't my Basic-plan credits run inbox placement?",
      a: (
        <>
          Basic tiers carry a feature gate set to <code>['detection']</code> — credits can
          only be spent on bot/anomaly detection. Upgrade to any Pro tier (same headline
          credit count, ~3× the pool size to cover the wider feature surface) to unlock
          verification, blacklist, inbox placement, and phone validation.
        </>
      ),
    },
    {
      q: 'Do my credits roll over?',
      a: (
        <>
          <strong>Monthly allowance:</strong> no — unused credits from your plan reset to
          zero on the 1st. <strong>Purchased credits</strong> (from credit packs):{' '}
          <strong>yes</strong>, they stack on top of the monthly allowance and never expire.
          The wallet drains the monthly bucket first, then dips into purchased credits, so
          you always lose the perishable balance first.
        </>
      ),
    },
    {
      q: 'When does Stripe charge me?',
      a: (
        <>
          <strong>Plan subscription:</strong> the first invoice fires at checkout for the
          full plan fee (pay-first, use-second), then again on each renewal date — monthly
          or annually per the interval you picked. Yearly billing is 10% off, and your{' '}
          <strong>first yearly invoice</strong> gets a one-time "2 months free" coupon
          (renewals charge the standard 10%-off rate).{' '}
          {chargeUpgradesImmediately ? (
            <>
              <strong>Mid-cycle upgrades</strong> (plan change or added Warmup mailbox) are
              charged immediately as a separate invoice; downgrades credit the unused
              portion of the tier onto your next invoice.
            </>
          ) : (
            <>
              <strong>Mid-cycle plan changes</strong> pro-rate onto your next invoice.
            </>
          )}{' '}
          <strong>Credit packs:</strong> one-shot charge at checkout, no recurring billing.
        </>
      ),
    },
    {
      q: 'Do prices include VAT?',
      a: (
        <>
          Prices are <strong>excl. VAT</strong>. Stripe Tax applies the right rule per
          customer at checkout: <strong>EU businesses</strong> with a valid VAT ID pay no
          VAT (reverse charge); <strong>non-EU customers</strong> pay no VAT;{' '}
          <strong>consumers</strong> and EU buyers without a VAT ID pay VAT at their
          country's rate.
        </>
      ),
    },
  ];

  return (
    <div className="mt-14">
      <h2 className="font-display text-2xl font-bold tracking-tight">Common questions</h2>
      <div className="mt-5 grid gap-3 md:grid-cols-2">
        {items.map((it) => (
          <div
            key={it.q}
            className="rounded-2xl border border-bordersoft bg-panel p-5 text-[13.5px]"
          >
            <div className="font-semibold text-txt">{it.q}</div>
            <div className="mt-1.5 leading-relaxed text-muted">{it.a}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
